import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { AgentAvatar } from "./AgentAvatar";

/**
 * Bell notification feed — real-time meaningful agent actions (reactive Convex
 * query). Mark items read individually (click) or all at once; unread auto-clear
 * at end of day via cron. Per-user read state.
 */
function severityColor(sev: string): string {
  if (sev === "error") return "var(--c-danger, #d32f2f)";
  if (sev === "warning") return "var(--c-warning, #ed6c02)";
  return "var(--c-link-accent, #1976d2)";
}

function timeAgo(ts: number, now: number): string {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function NotificationBell({ userEmail }: { userEmail: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const skip = !userEmail ? "skip" : { userEmail };
  const unread = useQuery(api.notifications.unreadCount, skip as any);
  const feed = useQuery(api.notifications.listFeed, skip as any);
  const markRead = useMutation(api.notifications.markRead);
  const markAllRead = useMutation(api.notifications.markAllRead);

  const count = unread ?? 0;
  const now = Date.now();

  return (
    <>
      <Tooltip title="Agent activity">
        <IconButton
          size="small"
          aria-label="Notifications"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            color: "var(--c-text-secondary)",
            border: "1px solid var(--c-border)",
            borderRadius: 2,
            width: 34,
            height: 34,
            transition: "all 0.2s ease",
            "&:hover": {
              color: "var(--c-link-accent)",
              borderColor: "var(--c-link-accent)",
              background: "var(--c-overlay-4)",
            },
          }}
        >
          <Badge badgeContent={count} color="error" max={99}>
            {count > 0 ? (
              <NotificationsActiveIcon sx={{ fontSize: 18 }} />
            ) : (
              <NotificationsIcon sx={{ fontSize: 18 }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480, overflow: "hidden" } } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Agent activity{count > 0 ? ` (${count} new)` : ""}
          </Typography>
          <Button
            size="small"
            startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
            disabled={count === 0}
            onClick={() => userEmail && markAllRead({ userEmail })}
          >
            Mark all read
          </Button>
        </Box>
        <Divider />
        <List dense sx={{ overflowY: "auto", maxHeight: 420, py: 0 }}>
          {feed === undefined && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">Loading…</Typography>
            </Box>
          )}
          {feed && feed.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">No agent activity yet</Typography>
            </Box>
          )}
          {feed && feed.map((n) => (
            <ListItemButton
              key={n._id}
              onClick={() => userEmail && !n.read && markRead({ userEmail, auditLogId: n._id })}
              sx={{
                alignItems: "flex-start",
                gap: 1.25,
                background: n.read ? "transparent" : "var(--c-overlay-4, rgba(25,118,210,0.06))",
              }}
            >
              <Box sx={{ position: "relative", flexShrink: 0, mt: 0.25 }}>
                <AgentAvatar agentId={n.agentId} size={34} />
                {!n.read && (
                  <Box sx={{ position: "absolute", top: -1, right: -1, width: 10, height: 10, borderRadius: "50%", border: "2px solid var(--c-bg-elevated, #fff)", background: severityColor(n.severity) }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 600, lineHeight: 1.35 }}>
                  {n.summary}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {(n as any).agentName ?? n.agentId.replace(/_/g, " ")} · {timeAgo(n.createdAt, now)}
                </Typography>
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  );
}
